import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse, HttpEventType } from "@angular/common/http";
import { Observable, throwError, EMPTY } from "rxjs";
import { catchError, switchMap, takeUntil, tap } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { Validators } from "@angular/forms";
import { StModalConfirmationComponent } from "~/dash/core/st-modal-confirmation/st-modal-confirmation.component";
import { RequestErrorCode } from "~/core/enum/request-error-code.enum";
import { StLoadingService } from "~/dash/core/st-loading.service";
import { SKIP_MFA } from "~/core/helpers/mfa.context-token";

@Injectable()
export class MfaInterceptor implements HttpInterceptor {
  constructor(
    private dialog: MatDialog,
    private translateService: TranslateService,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldHandleError(request, error)) {
          return this.handleMfaError(request, next, error);
        }
        return throwError(error);
      }),
    );
  }

  private shouldHandleError(request: HttpRequest<unknown>, error: HttpErrorResponse): boolean {
    if (request.context.get(SKIP_MFA)) {
      return false;
    }
    return error.error?.code === RequestErrorCode.INVALID_2FA_CODE;
  }

  private handleMfaError(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    StLoadingService.hide();
    const dialogRef = this.dialog.open(StModalConfirmationComponent, {
      disableClose: true,
    });

    dialogRef.componentInstance.message = this.translateService.instant("GENERAL.MFA_AUTH");
    dialogRef.componentInstance.title = this.translateService.instant("GENERAL.MFA_AUTH_TITLE");
    dialogRef.componentInstance.isOtpInput = true;
    dialogRef.componentInstance.validatorsLabel = [Validators.required];

    return dialogRef.componentInstance.closeFunc.pipe(
      switchMap((code: string | false) => {
        if (code) {
          dialogRef.componentInstance.isSaving = true;
          dialogRef.componentInstance.errorMessage = "";

          const currentBody = (request.body as any) || {};
          const newBody = { ...currentBody, auth_code: code };

          const newRequest = request.clone({
            body: newBody,
          });

          return next.handle(newRequest).pipe(
            tap({
              next: (event) => {
                if (event.type === HttpEventType.Response) {
                  dialogRef.close();
                }
              },
            }),
            catchError((err: HttpErrorResponse) => {
              dialogRef.componentInstance.isSaving = false;
              if (err.error?.code === RequestErrorCode.INVALID_2FA_CODE) {
                let errorMsg = err.error?.message || "Código inválido.";
                if (errorMsg === "Invalid two factor authentication code") {
                  errorMsg =
                    this.translateService.instant("GENERAL.MFA_INVALID_CODE") !== "GENERAL.MFA_INVALID_CODE"
                      ? this.translateService.instant("GENERAL.MFA_INVALID_CODE")
                      : "Código de autenticação de dois fatores inválido";
                }
                dialogRef.componentInstance.errorMessage = errorMsg;
                return EMPTY;
              }
              dialogRef.close();
              return throwError(() => err);
            }),
          );
        }

        dialogRef.close();
        return throwError(() => originalError);
      }),
      takeUntil(dialogRef.afterClosed()),
    );
  }
}
